import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MailCheck, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface EmailVerificationModalProps {
  email: string;
  onComplete: () => void;
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({ email, onComplete }) => {
  const { updateUser } = useAuth();
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setVerificationError('Lütfen 6 haneli kodu girin');
      return;
    }

    setVerifyingCode(true);
    setVerificationError('');
    setVerificationSuccess(false);

    try {
      const response = await api.verifyEmail(verificationCode);
      const verifiedUser = response.data?.user;
      if (verifiedUser) {
        updateUser({ ...verifiedUser, emailVerified: true });
      } else {
        updateUser({ emailVerified: true });
      }
      setVerificationSuccess(true);
      setTimeout(onComplete, 1500);
    } catch (err: unknown) {
      setVerificationError(err instanceof Error ? err.message : 'Doğrulama kodu geçersiz');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleResendCode = async () => {
    setResendingCode(true);
    setVerificationError('');
    setResendSuccess(false);
    try {
      await api.resendVerificationCode();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err: unknown) {
      setVerificationError(err instanceof Error ? err.message : 'Kod gönderilemedi');
    } finally {
      setResendingCode(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.92)',
          backdropFilter: 'blur(14px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)',
            borderRadius: '24px',
            padding: '40px 32px',
            maxWidth: '480px',
            width: '100%',
            border: '1px solid rgba(59, 130, 246, 0.35)',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.55)',
          }}
          initial={{ scale: 0.92, y: 16 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.92 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <div
              style={{
                width: '88px',
                height: '88px',
                borderRadius: '50%',
                background: 'rgba(30, 41, 59, 0.85)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                border: '2px solid rgba(59, 130, 246, 0.45)',
              }}
            >
              <MailCheck size={42} style={{ color: '#3b82f6' }} />
            </div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '10px', color: '#f8fafc' }}>
              Email Doğrulama
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '15px', lineHeight: 1.6 }}>
              <strong style={{ color: '#cbd5e1' }}>{email}</strong> adresine gönderilen 6 haneli kodu girin.
              Kod gelmediyse aşağıdan yeniden isteyebilirsiniz.
            </p>
          </div>

          {verificationError && (
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.35)',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '16px',
                color: '#f87171',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
              }}
            >
              <AlertCircle size={18} />
              {verificationError}
            </div>
          )}

          {(verificationSuccess || resendSuccess) && (
            <div
              style={{
                background: 'var(--accent-primary-soft)',
                border: '1px solid var(--accent-primary-glow)',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '16px',
                color: '#4ade80',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
              }}
            >
              <CheckCircle size={18} />
              {verificationSuccess ? 'Email doğrulandı!' : 'Yeni kod gönderildi.'}
            </div>
          )}

          <input
            type="text"
            inputMode="numeric"
            value={verificationCode}
            onChange={(e) => {
              setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              setVerificationError('');
            }}
            placeholder="000000"
            maxLength={6}
            autoFocus
            disabled={verifyingCode || verificationSuccess}
            style={{
              width: '100%',
              padding: '18px',
              background: 'rgba(30, 41, 59, 0.85)',
              border: '2px solid rgba(59, 130, 246, 0.35)',
              borderRadius: '12px',
              color: 'white',
              fontSize: '26px',
              fontWeight: 600,
              letterSpacing: '10px',
              textAlign: 'center',
              fontFamily: 'monospace',
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: '8px',
            }}
          />
          <p style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', marginBottom: '20px' }}>
            Kod 10 dakika geçerlidir
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              type="button"
              onClick={handleVerifyCode}
              disabled={verifyingCode || verificationCode.length !== 6 || verificationSuccess}
              style={{
                width: '100%',
                padding: '14px',
                background: 'var(--gradient-logo)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontWeight: 600,
                cursor: verifyingCode || verificationCode.length !== 6 || verificationSuccess ? 'not-allowed' : 'pointer',
                opacity: verifyingCode || verificationCode.length !== 6 || verificationSuccess ? 0.6 : 1,
              }}
            >
              {verifyingCode ? 'Doğrulanıyor...' : 'Doğrula'}
            </button>

            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendingCode || verificationSuccess}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(51, 65, 85, 0.55)',
                border: '1px solid rgba(71, 85, 105, 0.6)',
                borderRadius: '12px',
                color: '#cbd5e1',
                fontSize: '15px',
                fontWeight: 600,
                cursor: resendingCode || verificationSuccess ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <RefreshCw size={18} />
              {resendingCode ? 'Gönderiliyor...' : 'Kodu Yeniden Gönder'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmailVerificationModal;
